import React from "react";
import useEntity from "../../../hooks/useShipment";
import {
    Box, 
    Paper, 
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Link,
    TableSortLabel,
} from "@mui/material";
import { visuallyHidden } from '@mui/utils';
import { CheckBoxOutlined as CheckBoxOutlinedIcon, CheckBoxOutlineBlankOutlined as CheckBoxOutlineBlankOutlinedIcon } from "@mui/icons-material";
import {ID} from "../../../../types/global.d";
import EntityListHeader from "./ShipmentListHeader";
import EntityListSearch from "./ShipmentListSearch";
import {useLocation} from "react-router";
import ShipmentListPagination from "./ShipmentListPagination";
import * as Api from "../../../apis/Shipment.api";
import { Shipment } from "../../../types/models/Shipment";
import { PrimaryShipment } from "../../../types/models/Shipment";
import { usePagination } from '../../../../share';
import {displayDateTime, displayDate, displayTime} from "../../../../utils/formatDate.ts";
import {percentageFormat, fixedFormat, currencyFormat} from "../../../../utils/numberFormat";

import CuttingLink from "../../../../timber/components/cutting/CuttingDetail/CuttingLink";
import CuttingName from "../../../../timber/components/cutting/CuttingDetail/CuttingName";
import TimberStorageLink from "../../../../timber/components/timberStorage/TimberStorageDetail/TimberStorageLink";
import TimberStorageName from "../../../../timber/components/timberStorage/TimberStorageDetail/TimberStorageName";
import ContractorUserLink from "../../../../timber/components/contractorUser/ContractorUserDetail/ContractorUserLink";
import ContractorUserName from "../../../../timber/components/contractorUser/ContractorUserDetail/ContractorUserName";
import LocationLink from "../../../../location/components/location/LocationDetail/LocationLink";
import LocationName from "../../../../location/components/location/LocationDetail/LocationName";
import BuyerLink from "../../../../timber/components/buyer/BuyerDetail/BuyerLink";
import BuyerName from "../../../../timber/components/buyer/BuyerDetail/BuyerName";
import RoadCarrierLink from "../../../../timber/components/roadCarrier/RoadCarrierDetail/RoadCarrierLink";
import RoadCarrierName from "../../../../timber/components/roadCarrier/RoadCarrierDetail/RoadCarrierName";
import { toStringDisplay as toStringCuttingState } from "../../../types/models/CuttingState.d";
import { toStringDisplay as toStringShipmentState } from "../../../types/models/ShipmentState.d";
import { toStringDisplay as toStringShipmentType } from "../../../types/models/ShipmentType.d";
import { toStringDisplay as toStringUserType } from "../../../types/models/UserType.d";
import { toStringDisplay as toStringCordwoodMeasurementType } from "../../../types/models/CordwoodMeasurementType.d";

import { PrimaryCutting } from "../../../../timber/types/models/Cutting";
import { PrimaryTimberStorage } from "../../../../timber/types/models/TimberStorage";
import { PrimaryContractorUser } from "../../../../timber/types/models/ContractorUser";
import { PrimaryLocation } from "../../../../location/types/models/Location";
import { PrimaryBuyer } from "../../../../timber/types/models/Buyer";
import { PrimaryRoadCarrier } from "../../../../timber/types/models/RoadCarrier";
import {useTheme} from "@mui/material/styles";
import useSearchDebounce from "../../../../hooks/useSearchDebounce";
import ShipmentListItemTools from "./ShipmentListItemTools";
import {useStateSession} from "../../../../hooks/useAuth";
import GradientCircularProgress from "../../../../share/components/progress/GradientCircularProgress";

import useMediaQuery from '@mui/material/useMediaQuery';
import { Trans } from "@lingui/react/macro";
import {
    mdiLinkVariant,
} from "@mdi/js";
import {
    StyledFillNavLink,
    ToolbarTableCell,
    BigTableRow,
    BigTableHeaderRow,
    SmallTableRow,
    SmallTableHeaderRow,
    StyledTableCell,
    StyledMdiIcon,
} from "../../../../share/components/styled/Table";
import {OrderBy, OrderDirection} from "../../../../types/global.d";

type IProps = {
    showAdd: boolean,
    key?: string,
    reportKey?: string,
    shipmentAt?: Date,
    cuttingId?: ID,
    timberStorageId?: ID,
    contractorUserId?: ID,
    locationId?: ID,
    buyerId?: ID,
    roadCarrierId?: ID,
    };

const ShipmentListItem = (props: IProps) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    
    const session = useStateSession()
    const {showAdd, ...query} = props;

    const { page, pageSize, handleChangePage, handleChangePageSize } = usePagination('Shipment')

    
    
    
    
    const [, setSearchInputValue, searchInputTerm] = useSearchDebounce();
    const [searchInputAdvanced, setSearchInputAdvanced] = React.useState(undefined);

    const [orderBys, setOrderBys] = React.useState<OrderBy[]>([]);

    const toggleOrder = React.useCallback((name: string) => {
        setOrderBys(prev => {
            const current = prev.find(item => item.name === name)
            return [
                {
                    name,
                    direction: current?.direction === OrderDirection.Asc ? OrderDirection.Desc : OrderDirection.Asc
                },
                ...(prev.filter(item => item.name !== name))
            ]
        });
    }, []);

    const { data: dataSimple = {
        items: [],
        totalItems: 0,
        page,
        pageSize,
        numPages: 0
    }, 
        isLoading: isLoadingSimple,
        isFetching: isFetchingSimple,
        refetch: refetchSimple,
    } =
          Api.useGetShipmentPresentationQuery({
                  list: { page, pageSize },
                  filter: {
                    cuttingIdRefs: (PrimaryCutting.fromQuery(query.cuttingId)) ? [ PrimaryCutting.fromQuery(query.cuttingId) ] : [],
                    storageIdRefs: (PrimaryTimberStorage.fromQuery(query.timberStorageId)) ? [ PrimaryTimberStorage.fromQuery(query.timberStorageId) ] : [],
                    shippingContractorUserIdRefs: (PrimaryContractorUser.fromQuery(query.contractorUserId)) ? [ PrimaryContractorUser.fromQuery(query.contractorUserId) ] : [],
                    sourceLocationIdRefs: (PrimaryLocation.fromQuery(query.locationId)) ? [ PrimaryLocation.fromQuery(query.locationId) ] : [],
                    buyerIdRefs: (PrimaryBuyer.fromQuery(query.buyerId)) ? [ PrimaryBuyer.fromQuery(query.buyerId) ] : [],
                    roadCarrierIdRefs: (PrimaryRoadCarrier.fromQuery(query.roadCarrierId)) ? [ PrimaryRoadCarrier.fromQuery(query.roadCarrierId) ] : [],
                    key: searchInputTerm || '',
                    keyOr: true,
                    reportKey: searchInputTerm || '',
                    reportKeyOr: true,
                    // for now not supported to query here other types
                    shipmentAt: null,
                    orderBys,
                  },
              }, {skip: !!searchInputAdvanced})
    
    const { data: dataAdvanced = {
        items: [],
        totalItems: 0,
        page,
        pageSize,
        numPages: 0
        }, 
        isLoading: isLoadingAdvanced,
        isFetching: isFetchingAdvanced,
        refetch: refetchAdvanced,
    } =
        Api.useGetShipmentAdvancedPresentationQuery({
                list: { page, pageSize },
                filter: {

                    cuttingIdRefs: (PrimaryCutting.fromQuery(query.cuttingId)) ? [ PrimaryCutting.fromQuery(query.cuttingId) ] : [],
                    storageIdRefs: (PrimaryTimberStorage.fromQuery(query.timberStorageId)) ? [ PrimaryTimberStorage.fromQuery(query.timberStorageId) ] : [],
                    shippingContractorUserIdRefs: (PrimaryContractorUser.fromQuery(query.contractorUserId)) ? [ PrimaryContractorUser.fromQuery(query.contractorUserId) ] : [],
                    sourceLocationIdRefs: (PrimaryLocation.fromQuery(query.locationId)) ? [ PrimaryLocation.fromQuery(query.locationId) ] : [],
                    buyerIdRefs: (PrimaryBuyer.fromQuery(query.buyerId)) ? [ PrimaryBuyer.fromQuery(query.buyerId) ] : [],
                    roadCarrierIdRefs: (PrimaryRoadCarrier.fromQuery(query.roadCarrierId)) ? [ PrimaryRoadCarrier.fromQuery(query.roadCarrierId) ] : [],
                    ...searchInputAdvanced,
                    orderBys,
                },
            }, {skip: !searchInputAdvanced})
    const {data, isLoading, isFetching, refetch} =
        searchInputAdvanced ? 
            {
                data: dataAdvanced,
                isLoading: isLoadingAdvanced,
                isFetching: isFetchingAdvanced,
                refetch: refetchAdvanced,
            } 
            :
        {
                data: dataSimple,
                isLoading: isLoadingSimple,
                isFetching: isFetchingSimple,
                refetch: refetchSimple,
            }

    const routeLocation = useLocation();
    
    const onClickUrl = (entity: Shipment) => routeLocation.pathname.endsWith('shipments') ?
        `${PrimaryShipment.fromEntity(entity).toPath()}`
        :
        `shipments/${PrimaryShipment.fromEntity(entity).toPath()}`

    const onEditUrl = (entity: Shipment) => `${onClickUrl(entity)}/edit`

    
    
    return React.useMemo(() => (
        <Box>
            <Paper>
                <EntityListHeader showAdd={showAdd} {...query}/>
                <EntityListSearch
                    setSearchInputValue={setSearchInputValue}
                    searchInputAdvanced={searchInputAdvanced} setSearchInputAdvanced={setSearchInputAdvanced}
                    query={query}
                    refetch={refetch}
                />
                <TableContainer>

                    

                    

                    

                    

                    
        
                    
                    {isSmallScreen ?
                        <Table stickyHeader data-testid="Shipment-entity-list" size="small">
                            <TableHead>
                                <SmallTableHeaderRow>
                                    
                                    
                                    
                                        
                                    

                                    
                            <StyledTableCell align="left">
                                
                                <TableSortLabel
                                    active={ orderBys.find((item) => item.name === 'key') }
                                    direction={ orderBys.find((item) => item.name === 'key')?.direction || 'asc' }
                                    onClick={() => toggleOrder('key')}
                                >
                                
                                    <Trans>Key</Trans>
                                
                                </TableSortLabel>
                                
                            </StyledTableCell>
                        
                    
                                    
                                    
                                        
                                        
                                    

                                    
                                <StyledTableCell align="left">
                                    
                                    <TableSortLabel
                                        active={ orderBys.find((item) => item.name === 'buyerId') }
                                        direction={ orderBys.find((item) => item.name === 'buyerId')?.direction || 'asc' }
                                        onClick={() => toggleOrder('buyerId')}
                                    >
                                    
                                        <Trans>Buyer</Trans>
                                    
                                    </TableSortLabel>
                                    
                                </StyledTableCell>
                        
                    
                                    
                                    
                            <StyledTableCell align="center">
                                
                                <TableSortLabel
                                    active={ orderBys.find((item) => item.name === 'shipmentType') }
                                    direction={ orderBys.find((item) => item.name === 'shipmentType')?.direction || 'asc' }
                                    onClick={() => toggleOrder('shipmentType')}
                                >
                                
                                    <Trans>Shipment Type</Trans>
                                
                                </TableSortLabel>
                                
                            </StyledTableCell>
                        
                    
                                    
                                    
                            <StyledTableCell align="center">
                                
                                <TableSortLabel
                                    active={ orderBys.find((item) => item.name === 'state') }
                                    direction={ orderBys.find((item) => item.name === 'state')?.direction || 'asc' }
                                    onClick={() => toggleOrder('state')}
                                >
                                
                                    <Trans>State</Trans>
                                
                                </TableSortLabel>
                                
                            </StyledTableCell>
                        
                    
                                    
                                    
                                </SmallTableHeaderRow>
                            </TableHead>
                            <TableBody>
                                { data.items.map((item) => (
                                    <SmallTableRow
                                        hover={true}
                                        key={ PrimaryShipment.fromEntity(item).toPath() }
                                    >
                                        
                        
                        
                        
                        
                            
                        

                        <TableCell
                            scope="row"
                            align="left"
                        >
                            <Link
                                component={StyledFillNavLink}
                                to={ onClickUrl(item) }
                                viewTransition={true}
                            >
                            
                                <Typography component="div" sx={ { fontWeight: 'bold', } }>
                                    { item.key ?
                                        <>
                                            
                        
                        { item.key || '-' }
                        
                    
                                        </>
                                        :
                                        '-'
                                    }
                                </Typography>
                                
                            
                                <Typography component="div" variant="body1">
                                    { item.reportKey &&
                                            
                                <>
                                    <Trans>Report Key</Trans>:&nbsp;
                                    
                                    
                        
                        { item.reportKey || '-' }
                        
                    
                                </>
                                
                                }
                                </Typography>
                                
                            
                                <Typography component="div" variant="body1">
                                    { item.cuttingId &&
                                            
                                        <>
                                        <Trans>Cutting</Trans>:&nbsp;
                                        
                                        <CuttingName id={ item.cuttingId }/>
                                </>
                                
                                }
                                </Typography>
                                
                            
                            </Link>
                        </TableCell>

                    
                        
                    
                        
                        
                            
                            
                        

                        <TableCell
                            scope="row"
                            align="left"
                        >
                            <Link
                                component={StyledFillNavLink}
                                to={ onClickUrl(item) }
                                viewTransition={true}
                            >
                            
                                <Typography component="div" sx={ { fontWeight: 'bold', } }>
                                    { item.buyerId ?
                                        <>
                                            
                        
                        
                        <BuyerName id={ item.buyerId }/>
                        
                        
                    
                                        </>
                                        :
                                        '-'
                                    }
                                </Typography>
                                
                            
                                <Typography component="div" variant="body1">
                                    { item.shipmentAt &&
                                            
                                <>
                                    <Trans>at</Trans>:&nbsp;
                                    
                                    
                        
                        {displayDateTime(item.shipmentAt) }
                        
                    
                                </>
                                
                                }
                                </Typography>
                                
                            
                                <Typography component="div" variant="body1">
                                    { item.state &&
                                            
                                <>
                                    <Trans>State</Trans>:&nbsp;
                                    
                                    
                        
                        { toStringShipmentState(item.state) || '-' }
                        
                    
                                </>
                                
                                }
                                </Typography>
                                
                            
                            </Link>
                        </TableCell>

                    
                        
                    
                        
                        <TableCell
                            scope="row"
                            align="center"
                            
                        >
                            
                            <Link
                                component={StyledFillNavLink}
                                to={ onClickUrl(item) }
                                viewTransition={true}
                            >
                            
                            
                        
                        { toStringShipmentType(item.shipmentType) || '-' }
                        
                    
                            
                            </Link>
                            
                        </TableCell>
                        
                    
                        
                    
                        
                        <TableCell
                            scope="row"
                            align="center"
                            
                        >
                            
                            <Link
                                component={StyledFillNavLink}
                                to={ onClickUrl(item) }
                                viewTransition={true}
                            >
                            
                            
                        
                        { toStringShipmentState(item.state) || '-' }
                        
                    
                            
                            </Link>
                            
                        </TableCell>
                        
                    
                        
                    
                        
                    
                                    </SmallTableRow>
                                ))}
                            </TableBody>
                        </Table>
                        :
                    
                        <Table stickyHeader data-testid="Shipment-entity-list" size="small">
                            <TableHead>
                                <BigTableHeaderRow>
                                    
                                    
                                    
                                    <StyledTableCell align="left">
                                        
                                        <TableSortLabel
                                            active={ orderBys.find((item) => item.name === 'key') }
                                            direction={ orderBys.find((item) => item.name === 'key')?.direction || 'asc' }
                                            onClick={() => toggleOrder('key')}
                                        >
                                        
                                            <Trans>Key</Trans>
                                        
                                        </TableSortLabel>
                                        
                                    </StyledTableCell>
                                    
                                    
                                    
                                    
                                    <StyledTableCell align="left">
                                        
                                        <TableSortLabel
                                            active={ orderBys.find((item) => item.name === 'reportKey') }
                                            direction={ orderBys.find((item) => item.name === 'reportKey')?.direction || 'asc' }
                                            onClick={() => toggleOrder('reportKey')}
                                        >
                                        
                                            <Trans>Report Key</Trans>
                                        
                                        </TableSortLabel>
                                        
                                    </StyledTableCell>
                                    
                                    
                                    
                                    
                                    
                                    
                                    { query.storageId ?
                                        <></>
                                        :
                                        <StyledTableCell align="left">
                                            
                                            <TableSortLabel
                                                active={ orderBys.find((item) => item.name === 'storageId') }
                                                direction={ orderBys.find((item) => item.name === 'storageId')?.direction || 'asc' }
                                                onClick={() => toggleOrder('storageId')}
                                            >
                                            
                                                <Trans>Timber Storage</Trans>
                                            
                                            </TableSortLabel>
                                            
                                        </StyledTableCell>
                                    }
                                    
                                    
                                    
                                    
                                    <StyledTableCell align="center">
                                        
                                        <TableSortLabel
                                            active={ orderBys.find((item) => item.name === 'shipmentAt') }
                                            direction={ orderBys.find((item) => item.name === 'shipmentAt')?.direction || 'asc' }
                                            onClick={() => toggleOrder('shipmentAt')}
                                        >
                                        
                                            <Trans>Shipment At</Trans>
                                        
                                        </TableSortLabel>
                                        
                                    </StyledTableCell>
                                    
                                    
                                    
                                    
                                    
                                    
                                    
                                    
                                    
                                    
                                    { query.buyerId ?
                                        <></>
                                        :
                                        <StyledTableCell align="left">
                                            
                                            <TableSortLabel
                                                active={ orderBys.find((item) => item.name === 'buyerId') }
                                                direction={ orderBys.find((item) => item.name === 'buyerId')?.direction || 'asc' }
                                                onClick={() => toggleOrder('buyerId')}
                                            >
                                            
                                                <Trans>Buyer</Trans>
                                            
                                            </TableSortLabel>
                                            
                                        </StyledTableCell>
                                    }
                                    
                                    
                                    
                                    
                                    
                                    
                                    
                                    
                                    
                                    
                                    <StyledTableCell align="center">
                                        
                                        <TableSortLabel
                                            active={ orderBys.find((item) => item.name === 'state') }
                                            direction={ orderBys.find((item) => item.name === 'state')?.direction || 'asc' }
                                            onClick={() => toggleOrder('state')}
                                        >
                                        
                                            <Trans>State</Trans>
                                        
                                        </TableSortLabel>
                                        
                                    </StyledTableCell>
                                    
                                    
                                    
                                    
                                    <StyledTableCell align="center">
                                        
                                        <TableSortLabel
                                            active={ orderBys.find((item) => item.name === 'shipmentType') }
                                            direction={ orderBys.find((item) => item.name === 'shipmentType')?.direction || 'asc' }
                                            onClick={() => toggleOrder('shipmentType')}
                                        >
                                        
                                            <Trans>Shipment Type</Trans>
                                        
                                        </TableSortLabel>
                                        
                                    </StyledTableCell>
                                    
                                    
                                    
                                    
                                    
                                    
                                    
                                    
                                    
                                    
                                    
                                    
                                    
                                    
                                    
                                    
                                    
                                    
                                    
                                    
                                </BigTableHeaderRow>
                            </TableHead>
                            <TableBody>
                                { data.items.map((item) => (
                                    <BigTableRow
                                        hover={true}
                                        key={ PrimaryShipment.fromEntity(item).toPath() }
                                    >
                                        
                                        
                                        
                        <TableCell
                            scope="row"
                            align="left"
                            
                        >
                            
                            <Link
                                component={StyledFillNavLink}
                                to={ onClickUrl(item) }
                                viewTransition={true}
                            >
                            
                            
                        
                        { item.key || '-' }
                        
                    
                            
                            </Link>
                            
                        </TableCell>
                        
                    
                                        
                                        
                                        
                                        
                        <TableCell
                            scope="row"
                            align="left"
                            
                        >
                            
                            <Link
                                component={StyledFillNavLink}
                                to={ onClickUrl(item) }
                                viewTransition={true}
                            >
                            
                            
                        
                        { item.reportKey || '-' }
                        
                    
                            
                            </Link>
                            
                        </TableCell>
                        
                    
                                        
                                        
                                        
                                        
                                        
                                        
                        { query.storageId ? <></> :
                        
                        <TableCell
                            scope="row"
                            align="left"
                            
                        >
                            
                            
                        
                        
                        <TimberStorageLink id={ item.storageId } icon={<StyledMdiIcon d={mdiLinkVariant} />}/>
                        
                        
                    
                            
                        </TableCell>
                        
                        }
                        
                    
                                        
                                        
                                        
                                        
                        <TableCell
                            scope="row"
                            align="center"
                            
                        >
                            
                            <Link
                                component={StyledFillNavLink}
                                to={ onClickUrl(item) }
                                viewTransition={true}
                            >
                            
                            
                        
                        {displayDateTime(item.shipmentAt) }
                        
                    
                            
                            </Link>
                            
                        </TableCell>
                        
                    
                                        
                                        
                                        
                                        
                                        
                                        
                                        
                                        
                                        
                                        
                        { query.buyerId ? <></> :
                        
                        <TableCell
                            scope="row"
                            align="left"
                            
                        >
                            
                            
                        
                        
                        <BuyerLink id={ item.buyerId } icon={<StyledMdiIcon d={mdiLinkVariant} />}/>
                        
                        
                    
                            
                        </TableCell>
                        
                        }
                        
                    
                                        
                                        
                                        
                                        
                                        
                                        
                                        
                                        
                                        
                                        
                        <TableCell
                            scope="row"
                            align="center"
                            
                        >
                            
                            <Link
                                component={StyledFillNavLink}
                                to={ onClickUrl(item) }
                                viewTransition={true}
                            >
                            
                            
                        
                        { toStringShipmentState(item.state) || '-' }
                        
                    
                            
                            </Link>
                            
                        </TableCell>
                        
                    
                                        
                                        
                                        
                                        
                        <TableCell
                            scope="row"
                            align="center"
                            
                        >
                            
                            <Link
                                component={StyledFillNavLink}
                                to={ onClickUrl(item) }
                                viewTransition={true}
                            >
                            
                            
                        
                        { toStringShipmentType(item.shipmentType) || '-' }
                        
                    
                            
                            </Link>
                            
                        </TableCell>
                        
                    
                                        
                                        
                                        
                                        
                                        
                                        
                                        
                                        
                                        
                                        
                                        
                                        
                                        
                                        
                                        
                                        
                                        
                                        
                                        
                                        <ToolbarTableCell align="center" width="15%">
                                            <ShipmentListItemTools data={ item }
                                                                               onClickUrl={onClickUrl(item)} onEditUrl={onEditUrl(item)}
                                                                                />
                                        </ToolbarTableCell>
                                    </BigTableRow>
                                ))}
                            </TableBody>
                        </Table>
                    }
                    
            
                    { !data.totalItems && !isLoading && !isFetching &&
                        <Typography
                            sx={ { flex: '1 1 100%', padding: "2em", } }
                            component="div"
                        >
                            <Trans>No Shipments</Trans>
                        </Typography>
                    }
                    { (isLoading || isFetching) &&
                        <GradientCircularProgress/>
                    }
                </TableContainer>
                <ShipmentListPagination
                    totalItems={ data.totalItems }
                    page={Number(page)}
                    pageSize={pageSize}
                    handleChangePage={handleChangePage}
                    handleChangePageSize={handleChangePageSize}
                />
            </Paper>
        </Box>
    ), [isLoading, isFetching,isSmallScreen, data, page, pageSize, onClickUrl,  onEditUrl])
}

export default ShipmentListItem;
